/**
 * MobileSheet - Unified full-screen bottom sheet for mobile
 * Uses React Portal for guaranteed foreground rendering
 * Features: Swipe-to-close, body scroll lock, safe area support
 */
"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"

// Body scroll lock utility
function useScrollLock(enabled: boolean) {
  const scrollYRef = React.useRef(0);
  
  React.useEffect(() => {
    if (!enabled) return;
    
    // Save current scroll position
    scrollYRef.current = window.scrollY;
    
    // Lock body - just disable scrolling, scrollbar-gutter: stable handles the rest
    document.body.style.overflowY = 'hidden';
    
    return () => {
      // Unlock body
      document.body.style.overflowY = '';
      // Restore scroll position
      window.scrollTo({ top: scrollYRef.current, behavior: 'instant' });
    };
  }, [enabled]);
}

interface MobileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Title for accessibility */
  title?: string;
  /** Whether to show the drag handle */
  showHandle?: boolean;
  /** Custom className for the sheet container */
  className?: string;
  /** 
   * On desktop (lg+), render as a centered dialog instead of full-screen sheet 
   * Accepts a max-width value like '480px' or '560px'
   */
  desktopMaxWidth?: string;
}

export function MobileSheet({
  open,
  onOpenChange,
  children,
  title,
  showHandle = true,
  className,
  desktopMaxWidth
}: MobileSheetProps) {
  const [dragY, setDragY] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const touchStartY = React.useRef(0);
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState(false);
  
  // Lock body scroll when open
  useScrollLock(open);
  
  // Handle mounting for portal
  React.useEffect(() => {
    setMounted(true);
  }, []);
  
  // Close on escape
  React.useEffect(() => {
    if (!open) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);
  
  // Handle drag gesture
  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);
  
  const handleTouchMove = React.useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    // Only allow dragging down
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, [isDragging]);
  
  const handleTouchEnd = React.useCallback(() => {
    setIsDragging(false);
    // Close if dragged more than 100px
    if (dragY > 100) {
      onOpenChange(false);
    }
    setDragY(0);
  }, [dragY, onOpenChange]);
  
  if (!mounted || !open) return null;
  
  // Check if we're on desktop for the dialog mode
  const isDesktop = desktopMaxWidth ? window.matchMedia('(min-width: 1024px)').matches : false;
  
  const content = (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 animate-in fade-in duration-200"
        style={{ zIndex: 99990 }}
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      
      {/* Sheet - Full screen on mobile, centered dialog on desktop when desktopMaxWidth is set */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "fixed bg-white flex flex-col",
          "animate-in duration-300",
          !isDragging && "transition-transform duration-200",
          isDesktop 
            ? "rounded-2xl shadow-2xl slide-in-from-bottom-4"
            : "inset-0 slide-in-from-bottom",
          className
        )}
        style={{ 
          zIndex: 99999,
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          ...(isDesktop ? {
            top: '50%',
            left: '50%',
            transform: dragY > 0 
              ? `translate(-50%, -50%) translateY(${dragY}px)` 
              : 'translate(-50%, -50%)',
            width: `min(${desktopMaxWidth}, calc(100vw - 48px))`,
            maxHeight: '90vh',
            minHeight: '400px',
          } : {})
        }}
      >
        {/* Drag Handle - only on mobile */}
        {showHandle && !isDesktop && (
          <div 
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
            style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
          </div>
        )}
        
        {/* Content */}
        {children}
      </div>
    </>
  );
  
  // Render via portal directly to body for guaranteed foreground
  return createPortal(content, document.body);
}

// Sheet subcomponents for consistent structure
export function MobileSheetHeader({
  children,
  className,
  onClose
}: {
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}) {
  return (
    <div className={cn("flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0", className)}>
      {children}
      {onClose && (
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Schließen"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function MobileSheetContent({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div 
      className={cn("flex-1 overflow-y-auto overscroll-contain", className)}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {children}
    </div>
  );
}

export function MobileSheetFooter({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div 
      className={cn("flex-shrink-0 border-t border-gray-100 bg-white", className)}
      style={{ 
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        paddingTop: '16px',
        paddingLeft: 'max(20px, env(safe-area-inset-left))',
        paddingRight: 'max(20px, env(safe-area-inset-right))'
      }}
    >
      {children}
    </div>
  );
}
